import Axios from 'helpers/http'
import { generateJetpacB2BHostname } from 'helpers/http/generateHostname'

const JetpacB2BEnterpriseApi = new Axios(
  generateJetpacB2BHostname(),
  '/v1/b2b/enterprise'
)

export interface CampaignFAQItem {
  question: string
  answer: string
}

export interface CampaignRuleMetadata {
  allowedEmailDomains?: string[]
}

export interface CampaignDiscountConfig {
  discountValue: number
  discountType: string
  discountCurrencyValues?: Record<string, string>
  maxUsage: number
  promoCode: string
  summary: string
  promoRules?: number[]
}

export interface CreateCampaignPayload {
  name: string
  description: string
  prefix: string
  landingPageUrl: string
  currency: string
  totalCodes: number
  validityStartAt: number
  validityEndAt: number
  metadata: {
    otpVerify: boolean
    manualCodeEntry: boolean
  } & Record<string, unknown>
  faq?: CampaignFAQItem[]
  campaignType: string
  isNewUserOnly: boolean
  ruleMetadata?: CampaignRuleMetadata
  catalogId?: string
  maxRedemptions?: number
  generateQR: boolean
  uploadQRToS3: boolean
  discount?: CampaignDiscountConfig
}

export interface GenerateCampaignCodesPayload {
  catalogId: string
  totalCodes: number
  maxRedemptions: number
  generateQR: boolean
  uploadQRToS3: boolean
}

export interface Campaign {
  id: string
  name: string
  description?: string
  prefix: string
  landingPageUrl?: string
  currency: string
  totalCodes: number
  totalRedeemed?: number
  validityStartAt: number
  validityEndAt: number
  metadata?: unknown
  faq?: CampaignFAQItem[]
  campaignType?: string
  isNewUserOnly: boolean
  isActive: boolean
  ruleMetadata?: CampaignRuleMetadata
  createdBy?: string
  updatedBy?: string
  createdAt?: string
  updatedAt?: string
}

export interface CampaignCode {
  id: string
  campaignId: string
  catalogId: string
  code: string
  maxRedemptions: number
  currentRedemptions: number
  status: string
  redeemedByEmails?: string[]
  redeemedByUserIds?: string[]
  redeemedAt?: number
  createdAt?: string
  updatedAt?: string
}

interface ApiResponse<T> {
  data?: T
  message?: string
  status?: string
}

const getAuthHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`
})

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

class B2BCampaignApi {
  static async listCampaigns(
    faberToken: string,
    params: { offset?: number; limit?: number } = {}
  ) {
    const { offset = 0, limit = 20 } = params
    const response = await JetpacB2BEnterpriseApi.httpGet(
      `campaign?offset=${offset}&limit=${limit}`,
      getAuthHeaders(faberToken),
      true
    )

    return response.data as ApiResponse<{
      campaigns: Campaign[]
      total: number
      offset: number
      limit: number
    }>
  }

  static async getCampaign(faberToken: string, campaignId: string) {
    const response = await JetpacB2BEnterpriseApi.httpGet(
      `campaign/${campaignId}`,
      getAuthHeaders(faberToken),
      true
    )

    return response.data as ApiResponse<Campaign>
  }

  static async createCampaign(
    faberToken: string,
    payload: CreateCampaignPayload
  ) {
    const response = await JetpacB2BEnterpriseApi.axios({
      method: 'post',
      url: 'campaign',
      headers: getAuthHeaders(faberToken),
      data: payload,
      responseType: payload.generateQR ? 'blob' : 'json',
      withCredentials: true
    })

    const contentType = String(response.headers?.['content-type'] || '')

    if (contentType.includes('application/zip')) {
      const campaignId = response.headers?.['x-campaign-id'] || payload.prefix
      downloadBlob(response.data, `campaign-${campaignId}-qr-codes.zip`)

      return {
        status: response.statusText,
        message: 'Campaign created and QR ZIP downloaded',
        data: {
          campaignId
        }
      }
    }

    return response.data
  }

  static async generateCodes(
    faberToken: string,
    campaignId: string,
    payload: GenerateCampaignCodesPayload
  ) {
    const response = await JetpacB2BEnterpriseApi.axios({
      method: 'post',
      url: `campaign/${campaignId}/codes/generate`,
      headers: getAuthHeaders(faberToken),
      data: payload,
      responseType: payload.generateQR ? 'blob' : 'json',
      withCredentials: true
    })

    const contentType = String(response.headers?.['content-type'] || '')

    if (contentType.includes('application/zip')) {
      downloadBlob(response.data, `campaign-${campaignId}-qr-codes.zip`)

      return {
        status: response.statusText,
        message: 'Codes generated and QR ZIP downloaded',
        data: {
          totalCount: payload.totalCodes
        }
      }
    }

    return response.data
  }

  static async listCodes(
    faberToken: string,
    campaignId: string,
    params: { status?: string; offset?: number; limit?: number } = {}
  ) {
    const { status = '', offset = 0, limit = 100 } = params
    const queryParams = new URLSearchParams({
      offset: String(offset),
      limit: String(limit)
    })

    if (status) {
      queryParams.append('status', status)
    }

    const response = await JetpacB2BEnterpriseApi.httpGet(
      `campaign/${campaignId}/codes?${queryParams.toString()}`,
      getAuthHeaders(faberToken),
      true
    )

    return response.data as ApiResponse<{
      codes: CampaignCode[]
      total: number
      offset: number
      limit: number
    }>
  }

  static async exportCodesCSV(
    faberToken: string,
    campaignId: string,
    status?: string
  ) {
    const queryParams = new URLSearchParams()
    if (status) {
      queryParams.append('status', status)
    }
    const qs = queryParams.toString()
    const url = `campaign/${campaignId}/codes/export/csv${qs ? `?${qs}` : ''}`

    const response = await JetpacB2BEnterpriseApi.axios({
      method: 'get',
      url,
      headers: getAuthHeaders(faberToken),
      responseType: 'blob',
      withCredentials: true
    })

    downloadBlob(response.data, `campaign-${campaignId}-codes.csv`)
    return response
  }

  static async downloadQRCodes(faberToken: string, campaignId: string) {
    const response = await JetpacB2BEnterpriseApi.axios({
      method: 'get',
      url: `campaign/${campaignId}/codes/qr/download`,
      headers: getAuthHeaders(faberToken),
      responseType: 'blob',
      withCredentials: true
    })

    downloadBlob(response.data, `campaign-${campaignId}-qr-codes.zip`)
    return response
  }
}

export default B2BCampaignApi
